import { Briefcase, Video, FileText } from 'lucide-react';
import { Badge, Divider, type BadgeTone } from '@vnetwork/ui/primitives';
import type { JourneyNote, JourneyNegotiation } from '@/api/customer-journey';
import { formatDate } from '@/lib/format';
import { humanize } from '../../status-tones';
import { negotiationStatusLabel, negotiationTone } from '../sale-process-helpers';

/**
 * The read-only info sections that hang under specific board columns — linked
 * business trips + meeting tickets (Presentation), price negotiations (any
 * step), and contracted services (Contract). Mirrors the legacy
 * BusinessTripsSection / NegotiationsSection / ContractServicesSection.
 */

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 'var(--vn-font-size-xs)',
  fontWeight: 'var(--vn-font-weight-semibold)',
  color: 'var(--vn-fg-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--vn-space-1)',
};

const itemStyle: React.CSSProperties = {
  fontSize: 'var(--vn-font-size-xs)',
  color: 'var(--vn-fg-default)',
  padding: 'var(--vn-space-1) var(--vn-space-2)',
  borderRadius: 'var(--vn-radius-sm)',
  background: 'var(--vn-bg-subtle)',
};

function Section({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
      <Divider />
      {children}
    </div>
  );
}

/** Presentation column: linked business trips + online meeting tickets. */
export function BusinessTripsSection({
  businesses,
  tickets,
}: {
  businesses: JourneyNote[];
  tickets: JourneyNote[];
}) {
  if (!businesses.length && !tickets.length) return null;
  return (
    <Section>
      <span style={sectionLabelStyle}>
        <Briefcase size={12} /> Business trips ({businesses.length + tickets.length})
      </span>
      {businesses.map((n) => (
        <div key={`b-${n.customer_journey_note_id}`} style={itemStyle}>
          {n.business?.business_name || n.business?.business_id || '—'}
        </div>
      ))}
      {tickets.map((n) => (
        <div key={`t-${n.customer_journey_note_id}`} style={{ ...itemStyle, display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
          <Video size={12} style={{ color: 'var(--vn-fg-muted)' }} />
          {n.ticket?.ticket_title || n.ticket?.ticket_id || '—'}
        </div>
      ))}
    </Section>
  );
}

/** Any column: price negotiations bucketed to this step. */
export function NegotiationsSection({ negotiations }: { negotiations: JourneyNegotiation[] }) {
  if (!negotiations.length) return null;
  return (
    <Section>
      <span style={sectionLabelStyle}>Negotiations ({negotiations.length})</span>
      {negotiations.map((n) => (
        <div
          key={n.contract_negotiation_id}
          style={{ ...itemStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {n.contract_negotiation_title || n.contract_negotiation_id || '—'}
          </span>
          <Badge tone={negotiationTone(n.contract_negotiation_status) as BadgeTone} appearance="subtle" size="sm">
            {negotiationStatusLabel(n.contract_negotiation_status)}
          </Badge>
        </div>
      ))}
    </Section>
  );
}

/** Contract column: services created/contracted from this journey. */
export function ContractServicesSection({ services }: { services: JourneyNote[] }) {
  if (!services.length) return null;
  return (
    <Section>
      <span style={sectionLabelStyle}>
        <FileText size={12} /> Contract services ({services.length})
      </span>
      {services.map((n) => {
        const s = n.service;
        return (
          <div key={`s-${n.customer_journey_note_id}`} style={{ ...itemStyle, display: 'grid', gap: 'var(--vn-space-1)' }}>
            <span style={{ fontWeight: 'var(--vn-font-weight-medium)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s?.service_id} · {s?.service_name}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)', color: 'var(--vn-fg-muted)' }}>
              {s?.service_status ? humanize(s.service_status) : null}
              {s?.service_start_date ? ` · ${formatDate(s.service_start_date)}` : ''}
            </span>
          </div>
        );
      })}
    </Section>
  );
}
